import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { getCompanyInfo, updateCompanyInfo } from '../../../services/migration/companyDataService';
import { useTranslation } from 'react-i18next';

export interface CompanyDetailsData {
  companyName: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
}

interface CompanyDetailsModalProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  companyId: string | null;
}

type FormErrors = {
  [K in keyof CompanyDetailsData]?: boolean;
};

const initialCompanyDetails: CompanyDetailsData = {
  companyName: '',
  taxNumber: '',
  taxOffice: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  mersisNo: '',
  tradeRegistryNo: '',
};

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [details, setDetails] = useState<CompanyDetailsData>(initialCompanyDetails);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (open && companyId) {
      setLoading(true);
      setError(null);
      getCompanyInfo(companyId)
        .then((info) => {
          if (info) {
            setDetails({
              ...initialCompanyDetails,
              companyName: info.companyName || info.name || '',
              taxNumber: info.taxNumber || '',
              taxOffice: info.taxOffice || '',
              address: info.address || '',
              phone: info.phone || '',
              email: info.email || '',
              website: info.website || '',
              mersisNo: info.mersisNo || '',
              tradeRegistryNo: info.tradeRegistryNo || '',
            });
          } else {
            setDetails(initialCompanyDetails);
          }
        })
        .catch((err) => {
          console.error("Firma bilgileri yüklenirken hata:", err);
          setError(tt("Firma bilgileri yüklenirken bir hata oluştu.", "An error occurred while loading company details."));
          setDetails(initialCompanyDetails);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, companyId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
    
    const fieldName = name as keyof CompanyDetailsData;

    if (formErrors[fieldName]) {
      setFormErrors(prev => ({ ...prev, [fieldName]: false }));
    }
    
    const requiredFields: Array<keyof CompanyDetailsData> = ['companyName', 'taxNumber', 'taxOffice', 'address', 'phone'];
    if (error === tt("Lütfen tüm zorunlu alanları (*) doldurunuz.", "Please fill in all required fields (*).") && requiredFields.includes(fieldName)) {
      setError(null);
    }
  };

  const handleSave = () => {
    setFormErrors({});
    setError(null);

    const requiredFields: Array<keyof CompanyDetailsData> = [
      'companyName', 
      'taxNumber', 
      'taxOffice', 
      'address', 
      'phone'
    ];
    const newErrors: FormErrors = {};
    let hasError = false;

    requiredFields.forEach(field => {
      if (!details[field]) {
        newErrors[field] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setFormErrors(newErrors);
      setError(tt("Lütfen tüm zorunlu alanları (*) doldurunuz.", "Please fill in all required fields (*)."));
      return;
    }

    if (!companyId) {
      setError(tt("Şirket ID bulunamadı, kayıt yapılamıyor.", "Company ID not found, cannot save."));
      return;
    }
    setSaving(true);
    updateCompanyInfo(companyId, {
      name: details.companyName,
      companyName: details.companyName,
      taxNumber: details.taxNumber,
      taxOffice: details.taxOffice,
      address: details.address,
      phone: details.phone,
      email: details.email,
      website: details.website,
      mersisNo: details.mersisNo,
      tradeRegistryNo: details.tradeRegistryNo,
    })
      .then(() => {
        onClose(true);
      })
      .catch((err) => {
        console.error("Firma bilgileri kaydedilirken hata:", err);
        setError(tt("Firma bilgileri kaydedilirken bir hata oluştu: ", "An error occurred while saving company details: ") + err.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{tt('Firma Bilgileriniz', 'Your Company Details')}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && (
          <Grid container spacing={2} component="form">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="dense"
                name="companyName"
                label={tt("Firma Adı", "Company Name")}
                value={details.companyName}
                onChange={handleChange}
                variant="outlined"
                error={formErrors.companyName}
                helperText={formErrors.companyName ? tt("Firma adı zorunludur.", "Company name is required.") : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="dense"
                name="taxNumber"
                label={tt("Vergi Numarası / T.C. Kimlik No", "Tax Number / ID Number")}
                value={details.taxNumber}
                onChange={handleChange}
                variant="outlined"
                error={formErrors.taxNumber}
                helperText={formErrors.taxNumber ? tt("Vergi numarası zorunludur.", "Tax number is required.") : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="dense"
                name="taxOffice"
                label={tt("Vergi Dairesi", "Tax Office")}
                value={details.taxOffice}
                onChange={handleChange}
                variant="outlined"
                error={formErrors.taxOffice}
                helperText={formErrors.taxOffice ? tt("Vergi dairesi zorunludur.", "Tax office is required.") : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="tradeRegistryNo"
                label={tt("Ticaret Sicil No", "Trade Registry No")}
                value={details.tradeRegistryNo}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="mersisNo"
                label="Mersis No"
                value={details.mersisNo}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                margin="dense"
                name="address"
                label={tt("Adres", "Address")}
                multiline
                rows={3}
                value={details.address}
                onChange={handleChange}
                variant="outlined"
                error={formErrors.address}
                helperText={formErrors.address ? tt("Adres zorunludur.", "Address is required.") : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="dense"
                name="phone"
                label={tt("Telefon", "Phone")}
                value={details.phone}
                onChange={handleChange}
                variant="outlined"
                error={formErrors.phone}
                helperText={formErrors.phone ? tt("Telefon zorunludur.", "Phone is required.") : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                type="email"
                name="email"
                label={tt("E-posta", "Email")}
                value={details.email}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="website"
                label={tt("Web Sitesi", "Website")}
                value={details.website}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose()} color="inherit" disabled={saving}>{tt('İptal', 'Cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving || loading}>
          {saving ? <CircularProgress size={24} color="inherit" /> : tt('Kaydet', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDetailsModal; 